import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import { Search, Briefcase, MapPin, Building2, ChevronLeft, ChevronRight, ExternalLink, Filter, X, List, Grid, ChevronUp, ChevronDown } from 'lucide-react';
import JobBrowserSkeleton from '../components/layout/JobBrowserSkeleton';

// Employers can carry multiple industry tags. Prefer the full list; fall back to
// the single primary IndustryName for older payloads.
const getIndustryTags = (job) => {
  if (Array.isArray(job.Industries) && job.Industries.length > 0) return job.Industries;
  return job.IndustryName ? [job.IndustryName] : [];
};

export default function JobBrowser() {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const consumedOpenJobRef = useRef(null);
  const highlightedRowTimeoutRef = useRef(null);
  const jobRowRefs = useRef({});
    const perPage = 20;
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true); // only true until the very first fetch resolves
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
      location: '',
      title: '',
      company: '',
      industry: '',
      timeType: ''
  });
  const [dateMode, setDateMode] = useState('all'); // 'custom' | 'all'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // bump to trigger a fetch — only Search / Apply Filters / explicit actions touch this
  const [fetchNonce, setFetchNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState({ field: 'posted', direction: 'desc' });
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [pendingFocusedJobId, setPendingFocusedJobId] = useState(null);
  const [highlightedJobId, setHighlightedJobId] = useState(null);

  const fetchJobs = useCallback(async () => {
      if (!user) {
          return;
      }

      setLoading(true);

      try {
          const params = {
              page,
              per_page: perPage,
              status: 'active'
          };

          if (searchTerm.trim()) {
              params.search = searchTerm.trim();
          }

          Object.entries(filters).forEach(([key, value]) => {
              if (value) {
                  params[key] = value;
              }
          });

          // Users are always capped at 3 weeks ago
          const threeWeeksAgo = new Date();
          threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);
          const cap = threeWeeksAgo.toISOString().split('T')[0];

          if (dateMode === 'custom') {
              params.date_from = dateFrom && dateFrom > cap ? dateFrom : cap;
              if (dateTo) params.date_to = dateTo;
          } else {
              // 'last3weeks' or 'all' — both capped at 3 weeks ago
              params.date_from = cap;
          }

          const response = await api.get('/jobs/', { params });
          const data = response?.data || {};

          setJobs(Array.isArray(data.jobs) ? data.jobs : []);
          setTotal(Number(data.total || 0));
          setFilteredCount(Number(data.total || 0));
          setTotalPages(Number(data.total_pages || 0));
      } catch (error) {
          console.error('Error fetching jobs:', error);
          setJobs([]);
          setTotal(0);
          setFilteredCount(0);
          setTotalPages(0);
      } finally {
          setLoading(false);
          setInitialLoad(false);
      }
  }, [page, searchTerm, filters, dateMode, dateFrom, dateTo, user]);

  useEffect(() => {
      if (!user) {
          navigate('/login');
      }
  }, [navigate, user]);

  useEffect(() => {
      const fetchIndustries = async () => {
          try {
              const response = await api.get('/jobs/industries');
              setIndustries(Array.isArray(response?.data?.industries) ? response.data.industries : []);
          } catch (error) {
              console.error('Error fetching industries:', error);
              setIndustries([]);
          }
      };

      fetchIndustries();
  }, []);

  useEffect(() => {
      fetchJobs();
      // typing / filtering must not refetch — only page, the Search/Apply nonce, or auth
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fetchNonce, user]);

    useEffect(() => {
      const openJobId = location.state?.openJobId;
      const openJobTitle = location.state?.openJobTitle || '';
      const openJobCompany = location.state?.openJobCompany || '';
      if (!user || !openJobId || consumedOpenJobRef.current === openJobId) {
        return;
      }

      setSelectedJob(null);
      // The scroll/highlight machinery (jobRowRefs, highlightedJobId) only exists in
      // the list branch, so land in list view rather than grid.
      setViewMode('list');
      setSearchTerm('');
      const nextFilters = {
        location: '',
        title: openJobTitle,
        company: openJobCompany,
        industry: '',
        timeType: ''
      };
      setFilters(nextFilters);
      setPage(1);
      setFetchNonce((n) => n + 1);
      setPendingFocusedJobId(openJobId);
      consumedOpenJobRef.current = openJobId;
    }, [location.state, user]);

    useEffect(() => {
      if (viewMode !== 'list' || !pendingFocusedJobId) {
        return;
      }

      const focusJobId = pendingFocusedJobId;
      const targetRow = jobRowRefs.current[pendingFocusedJobId];
      if (!targetRow) {
        return;
      }

      targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedJobId(focusJobId);
      setPendingFocusedJobId(null);

      if (highlightedRowTimeoutRef.current) {
        clearTimeout(highlightedRowTimeoutRef.current);
      }

      highlightedRowTimeoutRef.current = setTimeout(() => {
        setHighlightedJobId((currentId) => (currentId === focusJobId ? null : currentId));
      }, 2200);
    }, [pendingFocusedJobId, jobs, viewMode]);

    useEffect(() => {
      return () => {
        if (highlightedRowTimeoutRef.current) {
          clearTimeout(highlightedRowTimeoutRef.current);
        }
      };
    }, []);

  const handleSearch = useCallback((event) => {
      event?.preventDefault();
      // the only path that triggers a fetch — reads current searchTerm / filters / dates
      setPage(1);
      setFetchNonce((n) => n + 1);
  }, []);

  const handleClearFilters = useCallback(() => {
      setSearchTerm('');
      setFilters({
          location: '',
          title: '',
          company: '',
          industry: '',
          timeType: ''
      });
      setDateMode('all');
      setDateFrom('');
      setDateTo('');
      setPage(1);
      setFetchNonce((n) => n + 1);
  }, []);

  const handleRemoveFilter = useCallback((key) => {
      setFilters((previousFilters) => ({ ...previousFilters, [key]: '' }));
      setPage(1);
      setFetchNonce((n) => n + 1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
      setPage(nextPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSort = useCallback((field) => {
      setSortBy((previousSort) => ({
          field,
          direction: previousSort.field === field && previousSort.direction === 'asc' ? 'desc' : 'asc'
      }));
  }, []);

  const handleApplied = useCallback(async () => {
      if (!selectedJob?.UniqueJobID) {
          return;
      }

      setApplyLoading(true);

      try {
          const response = await api.post(`/jobs/${selectedJob.UniqueJobID}/apply`);
          // const appliedCount = Number(response?.data?.applied_count || 0);

          // setJobs((previousJobs) => previousJobs.map((job) => (
          //     job.UniqueJobID === selectedJob.UniqueJobID
          //         ? { ...job, Applied: appliedCount }
          //         : job
          // )));
          setSelectedJob(null);
          console.log('Status:', response.status)
          console.log(response.data)

      } catch (error) {
          console.error('Error recording application:', error);
      } finally {
          setApplyLoading(false);
      }
  }, [selectedJob]);

  const handleNotYetApplied = useCallback(async () => {
      if (!selectedJob?.UniqueJobID) {
          return;
      }

      setAddLoading(true);

      try {
          const response = await api.post(`/jobs/${selectedJob.UniqueJobID}/add-to-tracker`);
          
          setSelectedJob(null);
          console.log('Status:', response.status)
          console.log(response.data)
      } catch (error) {
          console.error('Error adding application:', error);
      } finally {
          setAddLoading(false);
          setSelectedJob(null);
      }
  }, [selectedJob]);

  const sortedJobs = [...jobs].sort((leftJob, rightJob) => {
      const valueForField = (job, field) => {
          switch (field) {
              case 'title':
                  return job.JobTitle || '';
              case 'company':
                  return job.CompanyName || '';
              case 'location':
                  return job.JobLocation || '';
              case 'industry':
                  return job.IndustryName || '';
              case 'type':
                  return job.TimeType || '';
              case 'posted':
              default:
                  return new Date(job.JobPostedOn || job.CreatedOn || 0).getTime();
          }
      };

      const leftValue = valueForField(leftJob, sortBy.field);
      const rightValue = valueForField(rightJob, sortBy.field);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          return sortBy.direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
      }

      const comparison = String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: 'base' });
      return sortBy.direction === 'asc' ? comparison : -comparison;
  });

  const SortIcon = ({ field }) => {
      if (sortBy.field !== field) {
          return <div className="w-4" />;
      }

      return sortBy.direction === 'asc'
          ? <ChevronUp size={16} className="text-primary-600" />
          : <ChevronDown size={16} className="text-primary-600" />;
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (initialLoad) {
      return <JobBrowserSkeleton isDarkMode={isDarkMode} />;
  }

    return (
        <>
            <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
                <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
                    <header className="mb-2 md:mb-2">
                        <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-1 md:mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Job Browser</h1>
                        <div className={`opacity-70 ml-6 italic text-sm font-semibold mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                          <p>We search for the most relevant job postings for you every day and reference those posted within the last three days so you always have fresh opportunities to explore.</p>
                          <p className='mt-1'><span className='font-black mr-1'>Just a heads-up:</span> We're not the job poster, and listings may be removed at any time by the employer or platform that originally published them.</p>
                        </div>
                        <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                            {filteredCount > 0
                                ? `Showing ${filteredCount} of ${total.toLocaleString()} opportunities`
                                : `Explore ${total.toLocaleString()} opportunities`}
                        </p>
                    </header>

                    <div className={`rounded-2xl border shadow-sm p-4 mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Search by job title or company name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                                >
                                    Search
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                                        showFilters || activeFilterCount > 0
                                            ? 'bg-primary-600 text-white'
                                            : isDarkMode
                                                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                >
                                    <Filter size={18} />
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <span className="bg-white text-primary-600 px-2 py-0.5 rounded-full text-xs font-bold">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                                    title={viewMode === 'list' ? 'Switch to Grid View' : 'Switch to List View'}
                                >
                                    {viewMode === 'list' ? <Grid size={18} /> : <List size={18} />}
                                </button>
                            </div>
                        </form>
                    </div>

                    {showFilters && (
                        <div className={`rounded-2xl border shadow-sm p-6 mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>Advanced Filters</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowFilters(false)}
                                    className={isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Location</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., New York, Remote"
                                        value={filters.location}
                                        onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Job Title</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Software Engineer"
                                        value={filters.title}
                                        onChange={(e) => setFilters({ ...filters, title: e.target.value })}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Company</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Google, Apple"
                                        value={filters.company}
                                        onChange={(e) => setFilters({ ...filters, company: e.target.value })}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Industry</label>
                                    <select
                                        value={filters.industry}
                                        onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                                    >
                                        <option value="">All Industries</option>
                                        {industries.map((industry) => (
                                            <option key={industry.IndustryID} value={industry.IndustryName}>
                                                {industry.IndustryName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Job Type</label>
                                    <select
                                        value={filters.timeType}
                                        onChange={(e) => setFilters({ ...filters, timeType: e.target.value })}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                                    >
                                        <option value="">All Types</option>
                                        <option value="Full-time">Full-time</option>
                                        <option value="Part-time">Part-time</option>
                                        <option value="Contract">Contract</option>
                                        <option value="Internship">Internship</option>
                                        <option value="Temporary">Temporary</option>
                                    </select>
                                </div>
                            </div>

                            {/* Date range filter */}
                            {(() => {
                                const threeWeeksAgo = new Date();
                                threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);
                                const cap = threeWeeksAgo.toISOString().split('T')[0];
                                const today = new Date().toISOString().split('T')[0];
                                return (
                                    <div className="mt-4 flex flex-wrap items-center gap-3">
                                        <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Date posted:</span>
                                        {['all', 'custom'].map((mode) => (
                                            <button
                                                key={mode}
                                                type="button"
                                                onClick={() => {
                                                    setDateMode(mode);
                                                    if (mode === 'custom') {
                                                        setDateFrom(cap);
                                                        setDateTo(today);
                                                    }
                                                }}
                                                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                                                    dateMode === mode
                                                        ? 'bg-primary-600 text-white border-primary-600'
                                                        : isDarkMode
                                                            ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                                            : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                                }`}
                                            >
                                                {mode === 'custom' ? 'Custom Range' : 'All'}
                                            </button>
                                        ))}
                                        {dateMode === 'custom' && (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="date"
                                                    value={dateFrom}
                                                    min={cap}
                                                    max={today}
                                                    onChange={(e) => setDateFrom(e.target.value)}
                                                    className={`px-3 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                                                />
                                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>to</span>
                                                <input
                                                    type="date"
                                                    value={dateTo}
                                                    min={cap}
                                                    max={today}
                                                    onChange={(e) => setDateTo(e.target.value)}
                                                    className={`px-3 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            <div className="flex items-center justify-end gap-3 mt-4">
                                <button
                                    type="button"
                                    onClick={handleClearFilters}
                                    className={`px-4 py-2 font-medium ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-800'}`}
                                >
                                    Clear all
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSearch}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                                >
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    )}

                    {(activeFilterCount > 0 || searchTerm) && (
                      <div className="flex items-center gap-2 flex-wrap mb-6">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Active filters:</span>
                        {searchTerm && (
                          <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-full text-sm font-medium shadow-sm">
                            Search: "{searchTerm}"
                            <button
                              type="button"
                              onClick={() => { setSearchTerm(''); setPage(1); setFetchNonce((n) => n + 1); }}
                              className="hover:bg-primary-700 rounded-full p-0.5"
                            >
                              <X size={14} />
                            </button>
                          </span>
                        )}
                        {Object.entries(filters).map(([key, value]) => {
                          if (!value) return null;
                          const labels = {
                            location: 'Location',
                            title: 'Title',
                            company: 'Company',
                            industry: 'Industry',
                            timeType: 'Job Type'
                          };
                          return (
                            <span
                              key={key}
                              className="inline-flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-full text-sm font-medium shadow-sm"
                            >
                              {labels[key]}: {value}
                              <button
                                type="button"
                                onClick={() => handleRemoveFilter(key)}
                                className="hover:bg-primary-700 rounded-full p-0.5"
                              >
                                <X size={14} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    <div className="relative">
                    {loading && (
                        <div className={`absolute inset-0 z-10 flex items-center justify-center rounded-2xl ${isDarkMode ? 'bg-slate-900/60' : 'bg-white/60'} backdrop-blur-sm`}>
                            <div className={`flex items-center gap-3 px-4 py-2 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-700'}`}>
                                <span className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                                <span className="text-sm font-medium">Updating listings…</span>
                            </div>
                        </div>
                    )}
                    <div className={`transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
                    {viewMode === 'list' ? (
                        <div className={`rounded-md border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                            <div className="overflow-x-auto lg:overflow-x-visible">
                              <table className="w-full table-auto">
                                    <thead className={`${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200'} border-b`}>
                                      <tr>
                                        <th onClick={() => handleSort('title')} className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}>
                                          <div className="flex items-center gap-1">Job Title<SortIcon field="title" /></div>
                                        </th>
                                        <th onClick={() => handleSort('company')} className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}>
                                          <div className="flex items-center gap-1">Company<SortIcon field="company" /></div>
                                        </th>
                                        <th onClick={() => handleSort('location')} className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}>
                                          <div className="flex items-center gap-1">Location<SortIcon field="location" /></div>
                                        </th>
                                        <th onClick={() => handleSort('industry')} className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}>
                                          <div className="flex items-center gap-1">Industry<SortIcon field="industry" /></div>
                                        </th>
                                        <th onClick={() => handleSort('type')} className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}>
                                          <div className="flex items-center gap-1 ml-3">Type<SortIcon field="type" /></div>
                                        </th>
                                        <th onClick={() => handleSort('posted')} className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}>
                                          <div className="flex items-center gap-1">Posted<SortIcon field="posted" /></div>
                                        </th>
                                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                                        {sortedJobs.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className={`px-6 py-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                    No jobs found
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedJobs.map((job) => (
                                              <tr
                                                key={job.UniqueJobID}
                                                ref={(element) => {
                                                  if (element) {
                                                    jobRowRefs.current[job.UniqueJobID] = element;
                                                  } else {
                                                    delete jobRowRefs.current[job.UniqueJobID];
                                                  }
                                                }}
                                                className={`transition-all duration-700 ${
                                                  highlightedJobId === job.UniqueJobID
                                                    ? isDarkMode
                                                      ? 'bg-primary-900/50 ring-2 ring-inset ring-primary-400 shadow-[0_0_0_1px_rgba(52,211,153,0.5)]'
                                                      : 'bg-primary-100 ring-2 ring-inset ring-primary-500 shadow-[0_0_0_1px_rgba(5,150,105,0.25)]'
                                                    : isDarkMode
                                                      ? 'bg-slate-800/40 hover:bg-slate-800'
                                                      : 'bg-white hover:bg-slate-50'
                                                }`}
                                              >
                                                    <td className="px-6 py-4 align-middle">
                                                      <div className={`text-sm font-medium align-middle break-words ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{job.JobTitle}</div>
                                                    </td>
                                                    <td className={`px-6 py-4 text-sm align-middle break-words ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{job.CompanyName}</td>
                                                    <td className={`px-6 py-4 text-sm align-middle break-words ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{job.JobLocation || '-'}</td>
                                                    <td className={`px-6 py-4 text-sm align-middle break-words ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                      {(() => {
                                                        const tags = getIndustryTags(job);
                                                        if (tags.length === 0) return '-';
                                                        const shown = tags.slice(0, 2);
                                                        const rest = tags.length - shown.length;
                                                        return (
                                                          <span className="inline-flex flex-wrap items-center gap-1 break-words">
                                                            <span>{shown.join(', ')}</span>
                                                            {rest > 0 && (
                                                              <span
                                                                title={tags.slice(2).join(', ')}
                                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-200 text-slate-600'}`}
                                                              >
                                                                +{rest}
                                                              </span>
                                                            )}
                                                          </span>
                                                        );
                                                      })()}
                                                    </td>
                                                    <td className="px-6 py-4 align-middle"> 
                                                        {job.TimeType ? (
                                                            <span className={`inline-flex items-center justify-center px-2.5 w-[80px] py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-primary-900/40 text-primary-300' : 'bg-primary-100 text-primary-800'}`}>
                                                                {job.TimeType}
                                                            </span>
                                                        ) : <span className='ml-3'>-</span>}
                                                    </td>
                                                    <td className={`px-6 py-4 text-sm align-middle ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        {job.JobPostedOn ? new Date(job.JobPostedOn).toLocaleDateString() : job.CreatedOn ? new Date(job.CreatedOn).toLocaleDateString() : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium align-middle">
                                                        <a
                                                            href={job.JobLinkURL}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={() => setSelectedJob(job)}
                                                            className={`inline-flex items-center gap-1 ${isDarkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'}`}
                                                        >
                                                            {job.userApplied 
                                                              ? 'Applied' 
                                                              : job.userApplied === 0 ? 'Tracked' : 'View Job'} 
                                                            <ExternalLink size={14} />
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
                            {sortedJobs.length === 0 ? (
                                <div className={`col-span-full rounded-2xl border p-12 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <Briefcase size={48} className="mx-auto text-slate-300 mb-4" />
                                    <p className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No jobs found</p>
                                </div>
                            ) : (
                              sortedJobs.map((job) => (
                                <div
                                  key={job.UniqueJobID}
                                  className={`rounded-2xl border p-4 md:p-6 hover:border-primary-300 hover:shadow-xl transition-all duration-300 group ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                                >
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                      <h3 className={`text-base md:text-xl font-bold mb-1 group-hover:text-primary-600 transition-colors ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                        {job.JobTitle}
                                      </h3>
                                      <div className={`flex items-center gap-2 mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                        <Building2 size={16} />
                                        <span className="font-medium">{job.CompanyName}</span>
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                      {(() => {
                                        const tags = getIndustryTags(job);
                                        if (tags.length === 0) return null;
                                        const shown = tags.slice(0, 3);
                                        const rest = tags.length - shown.length;
                                        return (
                                          <div className="flex flex-wrap justify-end gap-1 max-w-[200px]">
                                            {shown.map((tag) => (
                                              <span
                                                key={tag}
                                                className={`px-3 py-1 rounded-full text-xs font-semibold ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-700'}`}
                                              >
                                                {tag}
                                              </span>
                                            ))}
                                            {rest > 0 && (
                                              <span
                                                title={tags.slice(3).join(', ')}
                                                className={`px-2 py-1 rounded-full text-xs font-semibold ${isDarkMode ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}
                                              >
                                                +{rest}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      {job.TimeType && (
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isDarkMode ? 'bg-primary-900/40 text-primary-300' : 'bg-primary-100 text-primary-700'}`}>
                                          {job.TimeType}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {job.JobLocation && (
                                    <div className={`flex items-center gap-2 mb-3 md:mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                      <MapPin size={16} />
                                      <span className="text-sm">{job.JobLocation}</span>
                                    </div>
                                  )}

                                  <div className={`flex items-center justify-between pt-3 md:pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                    <div className="flex flex-col gap-1">
                                      {job.JobPostedOn && (
                                        <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                          Posted: {new Date(job.JobPostedOn).toLocaleDateString()}
                                        </span>
                                      )}
                                      <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                        Added: {new Date(job.CreatedOn).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      <a
                                        href={job.JobLinkURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => setSelectedJob(job)}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 text-sm bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 hover:shadow-lg transition-all"
                                      >
                                        {job.userApplied 
                                          ? 'Applied' 
                                          : job.userApplied === 0 ? 'Tracked' : 'View Job'}
                                        <ExternalLink size={16} />
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                        </div>
                    )}
                    </div>
                    </div>

                  {totalPages > 1 && (
                    <div className={`rounded-2xl border shadow-sm px-4 py-3 md:px-6 md:py-4 flex flex-col sm:flex-row items-center justify-between gap-2 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                      <div className={`text-xs sm:text-sm text-center sm:text-left ${isDarkMode ? 'text-slate-400 sm:text-slate-300' : 'text-slate-500 sm:text-slate-700'}`}>
                        Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, filteredCount)} of {filteredCount} jobs
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <span className={`text-sm whitespace-nowrap px-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                          Page {page} of {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                          disabled={page === totalPages}
                          className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                        >
                          <ChevronRight size={20} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
            </div>

          {selectedJob && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
              <div className={`rounded-2xl border shadow-2xl p-8 max-w-xl w-full mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                  {selectedJob.userApplied ? "You already applied!" : "Did you apply?"}
                </h2>
                <p className={`mb-6 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  {selectedJob.userApplied ? "You can view and update your application status for " : "Did you submit an application for "}
                  <span className={`font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{selectedJob.JobTitle}</span>
                  {selectedJob.CompanyName && (
                    <> at <span className={`font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{selectedJob.CompanyName}</span></>
                  )}
                    {selectedJob.userApplied ? " in the Job Tracker tab." : "?"}
                </p>
                <div className="flex items-center gap-3 justify-end">
                    {selectedJob.userApplied 
                      ? (
                        <button
                          onClick={() => setSelectedJob(null)}
                          className={`px-5 py-2 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                        >
                          Close
                        </button>
                      ) 
                      : (
                        <>
                          <button
                            onClick={() => setSelectedJob(null)}
                            className={`px-5 py-2 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                          >
                            No, Not Interested
                          </button>
                          {selectedJob.userApplied !== 0 &&
                            <button
                              onClick={handleNotYetApplied}
                              disabled={addLoading}
                              className={`px-5 py-2 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                            >
                              {addLoading ? 'Adding...' : 'Not Yet, Add to Job Tracker'}
                            </button>
                          }
                          <button
                            onClick={handleApplied}
                            disabled={applyLoading}
                            className="px-5 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
                          >
                            {applyLoading ? 'Recording...' : 'Yes, I Applied'}
                          </button>
                        </>
                      )
                    }
                </div>
              </div>
            </div>
          )}
        </>
    );
}